import { useApiReactQuery } from '@/api/useApiWithReactQuery'
import { PROPERTY_SEARCH_QUERY_KEY } from '@partment/types'
import { useDebounce } from '@repo/ui'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

const ITEMS_PER_PAGE = 12
const DEFAULT_SELECTED_BEDROOM = 'All'
const DEFAULT_PROPERTY_TYPE = ''

interface Property {
  id: string | number
  name: string
  state: string
  city: string
  lat: string | number
  lng: string | number
  daily_rate: number
  bedrooms?: number
  [key: string]: unknown
}

interface ApiResponse {
  results?: Property[] | undefined
  count?: number
  next?: string | null
  previous?: string | null
  min_price_per_night?: number
  max_price_per_night?: number
}

interface UsePropertyListingParams {
  location?: string | null
  checkin?: string | null
  checkout?: string | null
  guests?: string | null
  adults?: string | null
  children?: string | null
  infants?: string | null
  initialSort?: string
  flex_days?: string | null
}

export function usePropertyListing({
  location,
  checkin,
  checkout,
  guests,
  infants,
  initialSort = '',
  flex_days,
}: UsePropertyListingParams) {
  const router = useRouter()
  const pathname = usePathname()
  const urlSearchParams = useSearchParams()

  // Filters are seeded from the URL so a refresh / shared link / back button
  // restores the full filter state, not just the page.
  const [searchProperties, setSearchProperties] = useState(
    () => urlSearchParams?.get('search') ?? '',
  )
  const debouncedSearch = useDebounce(searchProperties, 500)
  const [sortDataSet, setSortDataSet] = useState(
    () => urlSearchParams?.get('sort') || initialSort || 'reviews_high_to_low',
  )
  // Initialise the page from the URL (?page=N) so a refresh / shared link / back
  // button lands on the same page instead of snapping back to page 1.
  const [currentPage, setCurrentPage] = useState(() => {
    const fromUrl = parseInt(urlSearchParams?.get('page') ?? '1', 10)
    return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1
  })
  // Tracks the last-seen filter signature so the "reset to page 1 on filter
  // change" effect fires only on genuine user changes — not on the initial
  // mount (which would clobber the page restored from the URL) and not under
  // React StrictMode's double-invoke.
  const prevFiltersSignatureRef = useRef<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [totalProperties, setTotalProperties] = useState(0)
  const [nextUrl, setNextUrl] = useState<string | null>(null)
  const [previousUrl, setPreviousUrl] = useState<string | null>(null)
  const [selectedBedroom, setSelectedBedroom] = useState<string>(
    () => urlSearchParams?.get('bedrooms') || DEFAULT_SELECTED_BEDROOM,
  )
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>(
    () => urlSearchParams?.get('type') || DEFAULT_PROPERTY_TYPE,
  )

  // Price range state - will be populated from API response
  const [minMaxPrice, setMinMaxPrice] = useState<[number, number]>([0, 0])
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 0])
  const priceRangeInitialized = useRef(false)
  const [priceRangeManuallyChanged, setPriceRangeManuallyChanged] = useState(false)

  // Store the original min/max price from initial API response for reset purposes
  const [originalMinMaxPrice, setOriginalMinMaxPrice] = useState<[number, number]>([0, 0])

  // Debounce price range changes to avoid too many API calls
  const debouncedPriceRange = useDebounce(priceRange, 500)

  const { get } = useApiReactQuery()

  const effectiveMinPrice = priceRangeManuallyChanged ? debouncedPriceRange[0] : 0
  const effectiveMaxPrice = priceRangeManuallyChanged ? debouncedPriceRange[1] : 0

  // Normalize search value for consistent searchKey generation
  const normalizedSearch = useMemo(() => {
    return debouncedSearch ? debouncedSearch.trim() : ''
  }, [debouncedSearch])

  // Compose react-query key with filters and pagination info
  // Only include price range if it's been manually changed by user
  const searchKey = useMemo(
    () => [
      PROPERTY_SEARCH_QUERY_KEY,
      location,
      checkin,
      checkout,
      guests,
      sortDataSet,
      ITEMS_PER_PAGE,
      currentPage,
      infants,
      flex_days,
      effectiveMinPrice,
      effectiveMaxPrice,
      selectedBedroom,
      selectedPropertyType,
      normalizedSearch, // Use normalized search value
    ],
    [
      location,
      checkin,
      checkout,
      guests,
      sortDataSet,
      currentPage,
      infants,
      flex_days,
      effectiveMinPrice,
      effectiveMaxPrice,
      selectedBedroom,
      selectedPropertyType,
      normalizedSearch, // Use normalized search value
    ],
  )

  // Memoize the query URL to prevent unnecessary re-fetches
  const queryUrl = useMemo(() => {
    const queryParams = new URLSearchParams()
    if (location) queryParams.append('location', location)
    if (checkin) queryParams.append('checkin', checkin)
    if (checkout) queryParams.append('checkout', checkout)
    if (guests) queryParams.append('guests', guests)
    if (infants) queryParams.append('infants', infants)
    if (flex_days) queryParams.append('flex_days', flex_days)
    // Always include sort parameter (defaults to reviews_high_to_low)
    queryParams.append('sort', sortDataSet || 'reviews_high_to_low')
    // Add search parameter if user has entered text (use normalized search)
    if (normalizedSearch) {
      queryParams.append('search', normalizedSearch)
    }
    // Only include price parameters if user has manually changed the price range
    // This prevents double API calls on initial load
    if (priceRangeManuallyChanged && effectiveMinPrice > 0 && effectiveMaxPrice > 0) {
      queryParams.append('min_price', effectiveMinPrice.toString())
      queryParams.append('max_price', effectiveMaxPrice.toString())
    }
    // Add bedrooms parameter (default is '1')
    if (selectedBedroom) {
      if (selectedBedroom === 'All') {
        queryParams.append('bedrooms', '')
      } else {
        // If "4+", send 4 as minimum bedrooms, otherwise send the exact number
        const bedroomsValue = selectedBedroom.trim() === '4+' ? '4' : selectedBedroom.trim()
        queryParams.append('bedrooms', bedroomsValue)
      }
    }
    // Add property type parameter (default is 'apartment')
    if (selectedPropertyType) {
      queryParams.append('type', selectedPropertyType)
    }
    queryParams.append('limit', ITEMS_PER_PAGE.toString())
    queryParams.append('page', currentPage.toString())
    return `vacation-rental/search/?${queryParams.toString()}`
  }, [
    location,
    checkin,
    checkout,
    guests,
    infants,
    flex_days,
    sortDataSet,
    currentPage,
    effectiveMinPrice,
    effectiveMaxPrice,
    selectedBedroom,
    selectedPropertyType,
    normalizedSearch, // Use normalized search value
    priceRangeManuallyChanged,
  ])

  // Fetch data using the composed key and URL
  // Use a shorter staleTime for search queries to ensure fresh data when search changes
  const { data, isLoading } = get(
    searchKey,
    queryUrl,
    false,
    'v2' as unknown as null | undefined,
    {
      staleTime: normalizedSearch ? 2 * 60 * 1000 : 5 * 60 * 1000, // 2min cache for search, 5min otherwise
    },
  )

  // Extract properties from API response
  const properties = useMemo(() => {
    return Array.isArray((data as ApiResponse)?.results)
      ? (data as ApiResponse).results || []
      : []
  }, [data])

  useEffect(() => {
    if (data) {
      const apiResponse = data as ApiResponse
      setTotalProperties(apiResponse?.count || 0)
      setNextUrl(apiResponse?.next || null)
      setPreviousUrl(apiResponse?.previous || null)

      // Use API's min/max price range instead of calculating from properties
      const apiMinPrice = apiResponse?.min_price_per_night ?? 0
      const apiMaxPrice = apiResponse?.max_price_per_night ?? 0

      // The slider BOUNDS (min/max) are established ONCE per result set, guarded
      // by the synchronous `priceRangeInitialized` ref. A filtered response only
      // reports the min/max of the *filtered* set, so refreshing the bounds on
      // every response would collapse the slider down to the user's own
      // selection — e.g. drag max to 10k and 10k becomes the new ceiling, with
      // no way back. The ref is flipped to true the instant the user touches the
      // slider (in the exported setPriceRange wrapper), so it's race-free; it is
      // reset to false only when a non-price filter changes, which is exactly
      // when the bounds *should* re-expand to the new result set.
      if (!priceRangeInitialized.current) {
        if (apiMinPrice > 0 || apiMaxPrice > 0) {
          setMinMaxPrice([apiMinPrice, apiMaxPrice])
          setOriginalMinMaxPrice([apiMinPrice, apiMaxPrice])
          setPriceRange([apiMinPrice, apiMaxPrice])
          priceRangeInitialized.current = true
        } else if (properties?.length > 0) {
          // Fallback: derive bounds from the page's properties if the API didn't
          // provide a range.
          const prices = properties
            ?.map((item: Property) => Number(item.daily_rate || 0))
            ?.filter((n) => !isNaN(n))
          if (prices.length > 0) {
            const min = Math.min(...prices)
            const max = Math.max(...prices)
            setMinMaxPrice([min, max])
            setOriginalMinMaxPrice([min, max])
            setPriceRange([min, max])
            priceRangeInitialized.current = true
          }
        }
      }

      setIsLoadingMore(false)
    }
  }, [data, properties])

  // Reset to page 1 whenever a non-price filter / sort / search actually
  // changes. Comparing against the previous signature (vs. a "did mount" flag)
  // means the page restored from the URL survives the first render and any
  // StrictMode re-run.
  const filtersSignature = JSON.stringify([
    location,
    checkin,
    checkout,
    guests,
    sortDataSet,
    normalizedSearch,
    selectedBedroom,
    selectedPropertyType,
  ])
  useEffect(() => {
    if (prevFiltersSignatureRef.current === null) {
      prevFiltersSignatureRef.current = filtersSignature
      return
    }
    if (prevFiltersSignatureRef.current !== filtersSignature) {
      prevFiltersSignatureRef.current = filtersSignature
      setCurrentPage(1)
      priceRangeInitialized.current = false // Reset price range initialization on new search
      setPriceRangeManuallyChanged(false) // Reset manual change flag on new search
    }
  }, [filtersSignature])

  // Reset to page 1 only when the user *manually* changes the price range. The
  // manual flag stays false during the API-driven price initialisation, so this
  // never fires on load (which would otherwise clobber the URL page).
  useEffect(() => {
    if (priceRangeManuallyChanged) {
      setCurrentPage(1)
    }
  }, [debouncedPriceRange, priceRangeManuallyChanged])

  // Keep the URL in sync with the active filters + page (replace, not push, so
  // paging/filtering doesn't spam history) while preserving the search-box
  // params (location/checkin/…) already on the URL. Only non-default values are
  // written so a clean listing stays at a clean URL. Search uses the debounced
  // value so the URL doesn't churn on every keystroke.
  useEffect(() => {
    const params = new URLSearchParams(
      Array.from(urlSearchParams?.entries() ?? []),
    )
    const setOrDelete = (key: string, value: string, shouldSet: boolean) => {
      if (shouldSet) params.set(key, value)
      else params.delete(key)
    }
    setOrDelete('page', String(currentPage), currentPage > 1)
    setOrDelete('search', normalizedSearch, !!normalizedSearch)
    setOrDelete(
      'sort',
      sortDataSet,
      !!sortDataSet && sortDataSet !== 'reviews_high_to_low',
    )
    setOrDelete(
      'bedrooms',
      selectedBedroom,
      !!selectedBedroom && selectedBedroom !== DEFAULT_SELECTED_BEDROOM,
    )
    setOrDelete('type', selectedPropertyType, !!selectedPropertyType)
    const nextQuery = params.toString()
    if (nextQuery !== (urlSearchParams?.toString() ?? '')) {
      router.replace(`${pathname}${nextQuery ? `?${nextQuery}` : ''}`, {
        scroll: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    normalizedSearch,
    sortDataSet,
    selectedBedroom,
    selectedPropertyType,
  ])

  // Reset function for price filter
  const resetPriceFilter = () => {
    setPriceRange(originalMinMaxPrice);
    setPriceRangeManuallyChanged(false);
    priceRangeInitialized.current = true; // Mark as initialized
  };

  // No client-side filtering needed - all filtering is done by API
  // Return properties as-is from API response
  const filteredData: Property[] = properties

  // Calculate total pages based on backend totalProperties
  const totalPages = Math.ceil(totalProperties / ITEMS_PER_PAGE)

  // Use API's next/previous to determine pagination state
  const hasNext = !!nextUrl
  const hasPrevious = !!previousUrl

  // Paging indices for display (based on backend count and current page)
  const startItem = totalProperties > 0 ? (currentPage - 1) * ITEMS_PER_PAGE + 1 : 0
  const endItem = totalProperties > 0
    ? Math.min(
      startItem + properties.length - 1,
      totalProperties
    )
    : 0

  // Pagination handlers
  // Scroll to top after React commits a page change
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    ;(document.scrollingElement ?? document.documentElement).scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentPage])

  const handleNextPage = () => {
    if (!isLoadingMore && hasNext && nextUrl) {
      setIsLoadingMore(true)
      setCurrentPage((prev) => prev + 1)
    }
  }
  const handlePreviousPage = () => {
    if (!isLoadingMore && hasPrevious && previousUrl) {
      setIsLoadingMore(true)
      setCurrentPage((prev) => prev - 1)
    }
  }
  const handleFirstPage = () => {
    if (!isLoadingMore && currentPage > 1) {
      setIsLoadingMore(true)
      setCurrentPage(1)
    }
  }
  const handleLastPage = () => {
    if (!isLoadingMore && totalPages > 1 && currentPage < totalPages) {
      setIsLoadingMore(true)
      setCurrentPage(totalPages)
    }
  }
  const goToPage = (page: number) => {
    if (isLoadingMore || isLoading) return
    if (page < 1 || page > totalPages || page === currentPage) return
    setIsLoadingMore(true)
    setCurrentPage(page)
  }

  return {
    ITEMS_PER_PAGE,
    filteredData,
    isLoading,
    isLoadingMore,
    hasNext,
    hasPrevious,
    totalProperties,
    currentPage,
    totalPages,
    startItem,
    endItem,
    searchProperties,
    setSearchProperties,
    sortDataSet,
    setSortDataSet,
    selectedBedroom,
    setSelectedBedroom,
    selectedPropertyType,
    setSelectedPropertyType,
    minMaxPrice, // Use for slider's min/max
    priceRange, // Use for slider's value
    setPriceRange: (range: [number, number]) => {
      setPriceRange(range)
      priceRangeInitialized.current = true
      setPriceRangeManuallyChanged(true) // Mark as manually changed by user
    },
    resetPriceFilter, // Add reset function
    handleNextPage,
    handlePreviousPage,
    handleFirstPage,
    handleLastPage,
    goToPage,
  }
}
